import { Box, CircularProgress, Tooltip, Typography } from "@mui/material"
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid"
import { StyledButton } from "components/Button/StyledButton"
import { Article } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useEffect, useState } from "react"

const StockItems = () => {
  const { articles } = useStoreState((state) => state)
  const { fetchArticles } = useStoreActions((actions) => actions)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadStockItems = async () => {
      try {
        setLoading(true)
        setError(null)
        await fetchArticles({ fresh: true })
      } catch (err) {
        console.error("Error loading stock items:", err)
        setError("Failed to load stock items. Please try again later.")
      } finally {
        setLoading(false)
      }
    }

    loadStockItems()
  }, [fetchArticles])

  const handleRetry = async () => {
    try {
      setLoading(true)
      setError(null)
      await fetchArticles({ fresh: true })
    } catch (err) {
      console.error("Error reloading stock items:", err)
      setError("Failed to load stock items. Please try again later.")
    } finally {
      setLoading(false)
    }
  }

  const columns: GridColDef<Article>[] = [
    {
      field: "inventoryID",
      headerName: "Inv. ID",
      flex: 0.8,
      renderCell: (params: GridRenderCellParams<Article>) => (
        <Tooltip title={params.row.inventoryID || ""} placement="bottom-start">
          <Typography variant="body2" noWrap>
            {params.row.inventoryID || "N/A"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      renderCell: (params: GridRenderCellParams<Article>) => (
        <Tooltip title={params.row.description || ""} placement="bottom-start">
          <Typography variant="body2" noWrap>
            {params.row.description || "N/A"}
          </Typography>
        </Tooltip>
      ),
    },
  ]

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
        <StyledButton
          onClick={handleRetry}
          sx={{ mt: 2 }}
        >
          Retry
        </StyledButton>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Stock Items
      </Typography>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <DataGrid
          rows={articles}
          columns={columns}
          getRowId={(row) => row.id}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[5, 10, 25]}
        />
      </div>
    </Box>
  )
}

export default StockItems
